import React, { forwardRef, useRef } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import arrayMutators from 'final-form-arrays';
import { Form } from 'react-final-form';
import { Button } from 'ncoded-component-library';
import FilterIcon from 'icons/Filter.icon';
import TableControlButton from 'components/TableControlButton';
import TablePanelDropdown from 'components/TablePanelDropdown';
import useCallbackRef from 'hooks/useCallbackRef';
import MultiFilterOptions from 'components/MultiFilterModal/components/MultiFilterOptions';
import { MultiFilterOptionsProps } from 'components/MultiFilterModal/components/MultiFilterOptions/MultiFilterOptions.component';
import ClearFilters from 'components/ClearFilters';
import useMultiFilterForm from 'components/MultiFilterModal/hooks/useMultiFilterForm';
import { TablePanelDropdownRef } from 'components/TablePanelDropdown/TablePanelDropdown.component';
import { getHasElementsInArray } from 'utils';
import { FocusFieldRef, OpenFieldPanelRef } from 'models/FieldFocusableModal';
import useOpenFieldPanel from 'hooks/useOpenFieldPanel';
import { GetResultsCount } from 'components/MultiFilterModal/components/ResultsCount/ResultsCount.component';

import './MultiFilterPanel.styles.scss';

type MultiFilterPanelProps = {
  className?: string;
  pageParam?: string | string[];
  getResultsCount?: GetResultsCount;
} & MultiFilterOptionsProps;

const MultiFilterPanel: React.ForwardRefRenderFunction<
  OpenFieldPanelRef,
  MultiFilterPanelProps
> = (props, ref) => {
  const { className, filters, shouldSortFilters, pageParam, getResultsCount } =
    props;

  const classes = classNames(
    'multi-filter-panel',
    'table-panel-dropdown--form-panel',
    className,
  );

  const { t } = useTranslation();

  const [panel, panelRef] = useCallbackRef<TablePanelDropdownRef>();

  const multiFilterOptionsRef = useRef<FocusFieldRef>(null);

  useOpenFieldPanel(ref, panel, multiFilterOptionsRef);

  const { allFilters, initialValues, onSubmit } = useMultiFilterForm({
    filters,
    pageParam,
    onDone: panel?.close,
  });

  if (!getHasElementsInArray(filters)) return null;

  return (
    <Form
      mutators={{ ...arrayMutators }}
      initialValues={initialValues}
      onSubmit={onSubmit}
    >
      {({ invalid, handleSubmit }) => (
        <TablePanelDropdown
          ref={panelRef}
          className={classes}
          title={t('filters')}
          trigger={
            <div className="table-panel-dropdown__trigger-wrapper">
              <TableControlButton icon={FilterIcon} tooltip={t('filters')} />
            </div>
          }
          footer={
            <>
              <ClearFilters filters={allFilters} customOnClick={panel?.close} />
              <Button variant="link" type="button" onClick={panel?.close}>
                {t('cancel')}
              </Button>
              <Button disabled={invalid} onClick={handleSubmit}>
                {t('apply')}
              </Button>
            </>
          }
        >
          <form className="multi-filter-panel__body" onSubmit={handleSubmit}>
            <MultiFilterOptions
              ref={multiFilterOptionsRef}
              filters={filters}
              shouldSortFilters={shouldSortFilters}
              getResultsCount={getResultsCount}
            />
          </form>
        </TablePanelDropdown>
      )}
    </Form>
  );
};

export default forwardRef(MultiFilterPanel);
